import React, { useEffect, useState } from "react";
import { proxyAPI } from "../proxyAPI";
import TopicForm from "./TopicForm";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { Link } from "react-router-dom";
import ContactsIcon from "../../assets/icons/contact_edit.svg2";
import { shortUUID } from "../helpers";
import { ReportingTopicType } from "../store/slices/reportingSlice";
import MyPagination from "../Common/MyPagination";
import HelpButton from "../HelpContent/HelpButton";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export default function ReportingTopics() {
  const confirmDialog = useConfirmDialog();
  const [topics, setTopics] = useState<ReportingTopicType[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const getTopics = async (page = 0, perPage = 12) => {
    const data = await proxyAPI({
      prefix: "logic/reporting",
      url: `topics?pagination.perPage=${perPage}&pagination.page=${page}`,
    });
    if (data?.data) {
      setTotal(data.info.total);
      setTopics(data.data);
    }
  };

  const handleAddTopic = async () => {
    setTopics((p) => [
      {
        uuid: shortUUID(),
        label: "new topic",
        description: "some text",
        ticketStatuses: ["OK", "Not OK"],
        isDefault: false,
        isNew: true,
      },
      ...p,
    ]);
  };

  const handleDelete = async (topic: ReportingTopicType) => {
    if (
      !(await confirmDialog(
        "Delete topic",
        "Are you sure you want to delete topic?"
      ))
    ) {
      return;
    }
    if (topic.isNew) {
      setTopics((p) => p.filter((t) => t.uuid !== topic.uuid));
    } else {
      const data = await proxyAPI({
        prefix: "logic/reporting",
        url: "topics",
        method: "DELETE",
        body: { uuids: [topic.uuid] },
        setLoading,
      });
      if (data) {
        getTopics();
      }
    }
  };

  const handleSetDefault = (topicUUID: string) => {
    setTopics((prev) => [
      ...prev.map((t) => {
        if (t.uuid === topicUUID) {
          return { ...t, isDefault: true };
        }
        return { ...t, isDefault: false };
      }),
    ]);
  };

  useEffect(() => {
    getTopics();
  }, []);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center p-4">
        <div className="d-flex align-items-start">
          <ContactsIcon width={30} className="mt-2 me-3" />
          <div className="">
            <h1 className="fw-bold mb-1">Reporting Topics<HelpButton helpKey="reporting-topics" /></h1>
            <span className="opacity-50">
              Define the ticket based on the relevant topic or subject. Create
              the pre defined ticket statuses.
            </span>
          </div>
        </div>
        <div className="d-flex">
          <Link
            className="btn d-flex align-items-center bg-white rounded-3 py-2 ps-3 pe-5 me-3"
            to="/reporting/templates"
          >
            <ContactsIcon width={20} />
            <div className="border-end border-tertiary mx-2 align-self-stretch" />
            <div className="text-start small">
              <h6 className="fw-bold mb-0">Templates<HelpButton helpKey="reporting-templates" /></h6>
              <span className="small opacity-50">Manage</span>
            </div>
          </Link>
        </div>
      </div>
      <div className="p-4 my-2">
        <div className="text-end mb-2">
          <button
            type="button"
            className="btn btn-colored"
            onClick={handleAddTopic}
          >
            Add topic
          </button>
        </div>
        {topics.map((topic, index) => (
          <div key={topic.uuid} className="mb-3 bg-white p-3 rounded-3">
            <TopicForm
              topic={topic}
              handleDelete={handleDelete}
              isDefault={topic.isDefault}
              setDefault={() => handleSetDefault(topic.uuid)}
            />
          </div>
        ))}
      </div>
      <MyPagination onPaginate={getTopics} total={total} />
      <JsonCollapse json={topics} />
    </div>
  );
}
